import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Clock, ListChecks, CalendarRange, Calendar as CalIcon, ChevronRight as ChevronRightIcon, LayoutGrid } from "lucide-react";
import { MemberAvatar } from "@/components/Atoms";
import { BottomSheet } from "@/components/BottomSheet";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { usePaywall } from "@/context/PaywallContext";
import { useEntitlement } from "@/context/EntitlementsContext";
import { api, getCached } from "@/lib/api";
import { eventLocalYmd, fmtRelativeDate, fmtTime, groupByDay, localYmd, memberColor } from "@/lib/format";
import { itemTimestamp, isCompleted, KindChip, KindDot, styleOf } from "@/lib/itemChip";
import { EmptyHint, EventRow, MemberFilter, ViewTab } from "./Shared";
import { rescheduleEvent, useLongPressDrag } from "./drag";
import { DaySheet } from "./DaySheet";
import { CalendarFiltersButton } from "@/components/CalendarFiltersButton";
import { filterByEnabledTypes } from "@/components/PrimitiveTypeChips";
import { calendarItemRoute } from "@/lib/calendarRoute";

function toIsoDate(d) {
  // Local-timezone YYYY-MM-DD. Do NOT use .toISOString() here — that
  // converts to UTC and returns the wrong date string for users near
  // midnight in non-UTC timezones (root cause of the "click Friday →
  // opens Thursday" bug). Always use localYmd.
  return localYmd(d);
}

export function CalendarView({ refreshKey, onCreateAt, calendarKindFilter, hideToolbarExtras }) {
  const { family, members: familyMembers, user } = useAuth();
  const sub = useSubscription();
  const [view, setView] = useState("agenda");
  // Batch 40 — when this view is the Activities tab, we pre-fetch the
  // sub-calendars list and build a Set of `calendar_id`s whose `kind` is
  // in the allowed list. Events on other sub-cals are filtered out.
  const [kindAllowedCalIds, setKindAllowedCalIds] = useState(null);
  useEffect(() => {
    if (!calendarKindFilter || !family?.id) { setKindAllowedCalIds(null); return; }
    import("@/lib/calendarsApi").then(({ listCalendars }) => {
      listCalendars(family.id).then((cals) => {
        const allowed = new Set(
          (cals || [])
            .filter((c) => calendarKindFilter.includes(c.kind))
            .map((c) => c.id)
        );
        setKindAllowedCalIds(allowed);
      });
    });
  }, [family?.id, calendarKindFilter, refreshKey]);

  // Cache-first paint: read the LAST successful response synchronously
  // from the in-memory cache (hydrated from IndexedDB at app boot). If
  // present the screen renders INSTANTLY with that data; the network
  // refresh fires in the background and replaces it when fresh data lands.
  // Matches Today's pattern — the proven recipe for instant navigation.
  const cacheKey = family ? `/families/${family.id}/calendar-items` : null;
  const cachedRaw = cacheKey ? getCached(cacheKey) : null;
  const cachedNormalised = cachedRaw
    ? (cachedRaw || []).map((it) => ({ ...it, start_at: it.start_at || it.due_at || it.closes_at || null }))
    : null;

  const [events, setEvents] = useState(cachedNormalised || []);
  const [members, setMembers] = useState(familyMembers || []);
  const [loading, setLoading] = useState(!cachedNormalised);
  // Calendar views entitlement — Free is "agenda" only; Spark+ unlock "all".
  const calViews = useEntitlement("calendar.views");
  const { openPaywall } = usePaywall();
  const setPaywall = (on) => {
    if (on) openPaywall({
      featureKey: "calendar.views",
      requiredTier: calViews.requiredTier || "spark",
      message: "Week & Month views unlock with Spark. See your family's rhythm at a glance.",
    });
  };
  const [hiddenMembers, setHiddenMembers] = useState(new Set());
  const [hiddenCals, setHiddenCals] = useState(new Set());
  const [enabledTypes, setEnabledTypes] = useState(null); // null = all types
  const [daySheetDate, setDaySheetDate] = useState(null);

  // ─── Member filter initial state ───────────────────────────────
  // First run for a user defaults to "Just me" — the user's own day
  // should be the default view; everyone else's stuff is opt-in.
  // Once they touch the chips, that choice persists in localStorage
  // First-visit default for hidden members. We only INITIALIZE the set
  // (from localStorage or "everyone except me") on the very first render
  // per family — subsequent renders must NOT call setHiddenMembers from
  // here because passing a brand-new `new Set(...)` reference into setter
  // forces a re-render, which can re-trigger the effect if any dep ref
  // (familyMembers) shifts mid-render. That was the infinite loop the
  // user hit Feb 2026 on /plan/{chores,polls,todos}.
  const _hiddenInitedFor = useRef(null);
  useEffect(() => {
    if (!family?.id) return;
    if (_hiddenInitedFor.current === family.id) return;
    _hiddenInitedFor.current = family.id;
    try {
      const raw = localStorage.getItem(`kinly:cal-hidden:${family.id}`);
      if (raw !== null) {
        setHiddenMembers(new Set(JSON.parse(raw) || []));
        return;
      }
    } catch { /* fall through to "just me" default */ }
    const me = (familyMembers || []).find((m) => m.user_id === user?.id);
    if (me && familyMembers && familyMembers.length > 1) {
      const others = familyMembers.filter((m) => m.id !== me.id).map((m) => m.id);
      setHiddenMembers(new Set(others));
    } else {
      setHiddenMembers(new Set());
    }
  }, [family?.id, familyMembers, user?.id]);

  const toggleMember = (id) => {
    if (!family?.id) return;
    setHiddenMembers((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      localStorage.setItem(`kinly:cal-hidden:${family.id}`, JSON.stringify([...next]));
      return next;
    });
  };

  const setHiddenMembersDirect = (ids) => {
    if (!family?.id) return;
    const next = new Set(ids);
    localStorage.setItem(`kinly:cal-hidden:${family.id}`, JSON.stringify([...next]));
    setHiddenMembers(next);
  };

  const canUseView = (v) => v === "agenda" || (sub.quotas?.views || []).includes(v);

  const load = async () => {
    if (!family) return;
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth() - 2, 1).toISOString();
    const to = new Date(now.getFullYear(), now.getMonth() + 4, 0, 23, 59, 59).toISOString();
    try {
      // /calendar-items returns the FULL mix: events + chores + todos + med-doses
      // + polls. Calendar surfaces must never silently drop a timed chore or
      // todo (root cause of the "Buy dog food doesn't show on calendar" bug
      // reported 2026-06-11). Per-kind rendering and completion-hiding are
      // applied client-side below.
      // Members come from the shared AuthContext now — no per-screen refetch.
      const ev = await api.get(`/families/${family.id}/calendar-items`, { params: { from, to } });
      // Normalise: ensure every item has a `start_at` for the legacy
      // EventRow / drag handlers (they expect start_at). Use due_at /
      // closes_at as the timeline anchor for non-event kinds.
      const items = (ev.data || []).map((it) => ({
        ...it,
        start_at: it.start_at || it.due_at || it.closes_at || null,
      }));
      setEvents(items);
      // Sync shared members into local state (kept for prop drilling below)
      setMembers(familyMembers || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [family, refreshKey]);
  // Keep local members in sync when context updates (e.g. someone invited a kid)
  useEffect(() => { setMembers(familyMembers || []); }, [familyMembers]);

  // Optimistic patch for drag-reschedule callbacks; final state comes from reload.
  const onReschedule = async ({ eventId, startAt, endAt }) => {
    setEvents((arr) => arr.map((e) => e.id === eventId ? { ...e, start_at: startAt, end_at: endAt } : e));
    try {
      await rescheduleEvent(family.id, eventId, startAt, endAt);
    } finally {
      load();
    }
  };

  const trySetView = (v) => {
    if (canUseView(v)) setView(v);
    else setPaywall(true);
  };

  const filteredEvents = useMemo(() => {
    let arr = events;
    // Primitive-type chip filter (events/activities/study/chores/todos/polls).
    // Applies BEFORE the calendar-kind filter so kind filtering still works.
    if (enabledTypes) arr = filterByEnabledTypes(arr, enabledTypes);
    // Batch 40 — when the parent passed a calendarKindFilter (Activities tab),
    // hard-strip events whose calendar isn't in the allowed-kinds set. We
    // also drop non-event items (chores/todos/polls) entirely since the
    // Activities surface is intentionally "no routine noise".
    if (kindAllowedCalIds !== null) {
      arr = arr.filter((e) => {
        if (e.type && e.type !== "event") return false;
        const cid = e.calendar_id || null;
        return cid ? kindAllowedCalIds.has(cid) : false;
      });
    }
    // Sub-calendar filter (only applies to events that carry a calendar_id;
    // chores/todos/polls have no calendar concept and are always visible).
    if (hiddenCals.size > 0) {
      arr = arr.filter((e) => {
        if (e.type && e.type !== "event") return true;
        const cid = e.calendar_id || null;
        if (!cid) return true; // legacy / pre-Batch38 items pass through
        return !hiddenCals.has(cid);
      });
    }
    if (hiddenMembers.size === 0) return arr;
    return arr.filter((e) => {
      const pids =
        (e.participant_ids && e.participant_ids.length ? e.participant_ids : null) ||
        (e.assignee_person_ids && e.assignee_person_ids.length ? e.assignee_person_ids : null) ||
        (e.participant_person_ids && e.participant_person_ids.length ? e.participant_person_ids : null) ||
        (e.owner_person_id ? [e.owner_person_id] : null) ||
        // ── Fallback: legacy items with no assignee/participant. Treat them
        // as owned by their CREATOR so they're hidden when the creator
        // is hidden — instead of leaking to everyone.
        (e.created_by_person_id ? [e.created_by_person_id] : []);
      if (pids.length === 0) return false;
      return !pids.every((p) => hiddenMembers.has(p));
    });
  }, [events, hiddenMembers, hiddenCals, kindAllowedCalIds, enabledTypes]);

  // Per-view filter: completed items are shown ONLY in Agenda (strikethrough
  // for productivity glance). Day / Week / Month hide them to keep the
  // grid clean. UX spec from the user: "marked complete = removed from
  // calendar/day/week views, but in Agenda use strikethrough so the user
  // can still see how productive the day was".
  const visibleItems = useMemo(() => {
    if (view === "agenda") return filteredEvents;
    return filteredEvents.filter((i) => !isCompleted(i));
  }, [filteredEvents, view]);

  return (
    <div className="space-y-4">
      <div className="flex bg-secondary rounded-full p-1 text-sm" data-testid="calendar-view-tabs">
        <ViewTab id="agenda"  current={view} setCurrent={trySetView} locked={false} icon={ListChecks} label="Agenda" />
        <ViewTab id="day"     current={view} setCurrent={trySetView} locked={!canUseView("day")}   icon={Clock} label="Day" />
        <ViewTab id="week"    current={view} setCurrent={trySetView} locked={!canUseView("week")}  icon={CalendarRange} label="Week" />
        <ViewTab id="month"   current={view} setCurrent={trySetView} locked={!canUseView("month")} icon={CalIcon} label="Month" />
      </div>

      <div className="flex items-center gap-2 -mx-4 px-4 pb-1">
        <div className="flex-1 min-w-0">
          <MemberFilter
            members={members}
            hidden={hiddenMembers}
            onToggle={toggleMember}
            onSetHidden={setHiddenMembersDirect}
          />
        </div>
        <div className="shrink-0">
          <CalendarFiltersButton
            refreshKey={refreshKey}
            onTypesChange={setEnabledTypes}
            onCalendarsHiddenChange={(ids) => setHiddenCals(new Set(ids))}
            hideTypes={!!calendarKindFilter}
          />
        </div>
      </div>

      {loading ? (
        <div className="kinly-card h-32 skeleton" />
      ) : view === "agenda" ? (
        <AgendaView events={visibleItems} members={members} onDayTap={setDaySheetDate} />
      ) : view === "day" ? (
        <DayView events={visibleItems} members={members} onReschedule={onReschedule} onCreateAt={onCreateAt} />
      ) : view === "week" ? (
        <WeekView events={visibleItems} members={members} onReschedule={onReschedule} onDayTap={setDaySheetDate} />
      ) : (
        <MonthView events={visibleItems} members={members} onDayTap={setDaySheetDate} />
      )}

      <DaySheet
        open={!!daySheetDate}
        date={daySheetDate}
        members={members}
        onAddEvent={(d) => { setDaySheetDate(null); onCreateAt?.(d); }}
        onClose={() => setDaySheetDate(null)}
      />
    </div>
  );
}

function AgendaView({ events, members, onDayTap }) {
  const upcoming = events.filter((e) => new Date(e.start_at) >= new Date(new Date().toDateString()));
  if (upcoming.length === 0) {
    return (
      <EmptyHint
        eyebrow="Calendar"
        headline="Nothing planned yet."
        body={'Ask Kinly to add something — "piano every Tuesday 4pm" — or use the + button.'}
      />
    );
  }
  return (
    <div className="space-y-3">
      {groupByDay(upcoming).map(([day, items]) => (
        <article key={day} className="kinly-card" data-testid={`day-${day}`}>
          <button
            type="button"
            onClick={() => onDayTap?.(toIsoDate(items[0].start_at))}
            data-testid={`day-heading-${day}`}
            className="w-full flex items-center justify-between font-display text-[15px] font-medium tracking-tight mb-2 tap text-foreground hover:text-primary transition-colors group"
          >
            <span>{fmtRelativeDate(items[0].start_at)}</span>
            <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-full bg-primary/10 text-primary border border-primary/20 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
              <LayoutGrid className="w-3 h-3" /> Open day
              <ChevronRightIcon className="w-3 h-3" />
            </span>
          </button>
          <ul className="divide-y divide-border/70">
            {items.map((e) => <EventRow key={e.id} event={e} members={members} />)}
          </ul>
        </article>
      ))}
    </div>
  );
}

function WeekView({ events, members, onReschedule, onDayTap }) {
  const now = new Date();
  const day = (now.getDay() + 6) % 7;
  const monday = new Date(now); monday.setDate(now.getDate() - day); monday.setHours(0, 0, 0, 0);
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday); d.setDate(monday.getDate() + i); return d;
  });
  const byDay = days.map((d) => {
    const k = localYmd(d);
    return { date: d, items: events.filter((e) => eventLocalYmd(e.start_at) === k) };
  });

  const handleDrop = ({ payload, dropTarget }) => {
    const eventId = payload?.eventId;
    const dayKey = dropTarget.getAttribute("data-drop-day");
    if (!eventId || !dayKey || !onReschedule) return;
    const original = events.find((x) => x.id === eventId);
    if (!original) return;
    const orig = new Date(original.start_at);
    const [yy, mm, dd] = dayKey.split("-").map(Number);
    const next = new Date(yy, mm - 1, dd, orig.getHours(), orig.getMinutes(), 0, 0);
    const durMs = original.end_at ? (new Date(original.end_at) - orig) : 60 * 60000;
    onReschedule({ eventId, startAt: next.toISOString(), endAt: new Date(next.getTime() + durMs).toISOString() });
  };

  return (
    <div className="space-y-2" data-testid="week-view">
      {byDay.map(({ date, items }) => {
        const isToday = date.toDateString() === new Date().toDateString();
        const dropKey = localYmd(date);
        return (
          <article
            key={dropKey}
            data-testid={`week-day-${dropKey}`}
            data-drop-target="week"
            data-drop-day={dropKey}
            className={[
              "kinly-card p-3",
              isToday ? "border-primary/40 bg-primary/[0.04]" : "",
            ].join(" ")}
          >
            <div className="flex items-baseline justify-between mb-1 gap-2">
              <button
                type="button"
                onClick={() => onDayTap?.(dropKey)}
                data-testid={`week-day-heading-${dropKey}`}
                className="font-display text-[13px] tracking-tight tap hover:text-primary transition-colors text-left flex-1 min-w-0"
              >
                {date.toLocaleDateString(undefined, { weekday: "long" })}
                <span className="ml-2 text-muted-foreground font-normal">
                  {date.toLocaleDateString(undefined, { day: "numeric", month: "short" })}
                </span>
              </button>
              <button
                type="button"
                onClick={() => onDayTap?.(dropKey)}
                data-testid={`week-day-open-${dropKey}`}
                className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 hover:bg-primary hover:text-primary-foreground transition-colors shrink-0"
                aria-label={`Open day plan for ${dropKey}`}
              >
                <LayoutGrid className="w-3 h-3" /> Open
              </button>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground tabular-nums">{items.length || "·"}</span>
            </div>
            {items.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">Quiet day. Hold-drag an event here.</p>
            ) : (
              <ul className="divide-y divide-border/60">
                {items.map((e) => (
                  <DraggableEventRow key={e.id} event={e} members={members} onDrop={handleDrop} />
                ))}
              </ul>
            )}
          </article>
        );
      })}
    </div>
  );
}

function DraggableEventRow({ event, members, onDrop }) {
  const drag = useLongPressDrag({
    payload: { eventId: event.id },
    onDrop,
    label: event.title,
  });
  return (
    <li
      data-testid={`week-event-${event.id}`}
      {...drag.bind}
      className={drag.dragging ? "select-none" : ""}
    >
      <EventRow event={event} members={members} />
    </li>
  );
}

function DayView({ events, members, onReschedule, onCreateAt }) {
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setHours(0,0,0,0); return d; });
  const dayStart = new Date(cursor); dayStart.setHours(0,0,0,0);
  const dayEnd = new Date(cursor); dayEnd.setHours(23,59,59,999);

  const dayEvents = useMemo(() => events.filter((e) => {
    if (!e.start_at) return false;
    const s = new Date(e.start_at);
    return s >= dayStart && s <= dayEnd;
  }).sort((a,b) => (a.start_at || "").localeCompare(b.start_at || "")), [events, cursor]);

  const goDay = (delta) => { const d = new Date(cursor); d.setDate(d.getDate() + delta); setCursor(d); };
  const isToday = cursor.toDateString() === new Date().toDateString();

  // Full 24h grid (00:00 → 23:00). Per user request 2026-02-13: a day is
  // a day — don't hide the early-morning or late-night hours.
  const HOURS = Array.from({ length: 24 }, (_, i) => i);
  const PX_PER_HOUR = 44;

  const handleDrop = ({ payload, dropTarget, x, y }) => {
    const eventId = payload?.eventId;
    if (!eventId || !onReschedule) return;
    const original = events.find((x) => x.id === eventId);
    if (!original) return;
    const rect = dropTarget.getBoundingClientRect();
    const offsetY = y - rect.top;
    // Snap to 15-minute increments. Grid starts at 00:00 (top = 0min).
    const minutesFromStart = Math.max(0, Math.round(offsetY / PX_PER_HOUR * 60 / 15) * 15);
    const startAt = new Date(cursor);
    startAt.setHours(0, 0, 0, 0);
    startAt.setMinutes(minutesFromStart);
    const origStart = new Date(original.start_at);
    const durMs = original.end_at ? (new Date(original.end_at) - origStart) : 60 * 60000;
    onReschedule({
      eventId,
      startAt: startAt.toISOString(),
      endAt: new Date(startAt.getTime() + durMs).toISOString(),
    });
  };

  const onSlotTap = (hour) => {
    if (!onCreateAt) return;
    const d = new Date(cursor);
    d.setHours(hour, 0, 0, 0);
    onCreateAt(d);
  };

  return (
    <div data-testid="day-view" className="pb-24">
      <div className="flex items-center justify-between mb-3 px-1">
        <button data-testid="day-prev" onClick={() => goDay(-1)} className="w-9 h-9 tap rounded-full inline-flex items-center justify-center text-muted-foreground hover:bg-secondary">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="text-center">
          <h3 className="font-display text-[17px] font-medium tracking-tight">
            {cursor.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" })}
          </h3>
          {!isToday && (
            <button data-testid="day-today" onClick={() => setCursor(new Date(new Date().setHours(0,0,0,0)))} className="text-[11px] text-primary uppercase tracking-wider mt-0.5 tap">Jump to today</button>
          )}
        </div>
        <button data-testid="day-next" onClick={() => goDay(1)} className="w-9 h-9 tap rounded-full inline-flex items-center justify-center text-muted-foreground hover:bg-secondary">
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      <div className="relative" data-drop-target="day">
        {HOURS.map((h) => (
          <button
            key={h}
            type="button"
            data-testid={`day-slot-${h}`}
            onClick={() => onSlotTap(h)}
            className="w-full flex border-t border-border/40 text-left tap hover:bg-secondary/40 transition-colors"
            style={{ minHeight: PX_PER_HOUR }}
            aria-label={`Create at ${String(h).padStart(2,"0")}:00`}
          >
            <span className="w-12 -mt-2 text-[10px] tabular-nums text-muted-foreground font-mono pr-2 shrink-0">
              {String(h).padStart(2,"0")}:00
            </span>
            <span className="flex-1" />
          </button>
        ))}
        <div className="absolute inset-0 ml-12 pointer-events-none">
          {dayEvents.map((e) => (
            <DayEventBlock
              key={e.id}
              event={e}
              members={members}
              pxPerHour={PX_PER_HOUR}
              navigate={navigate}
              onDrop={handleDrop}
            />
          ))}
        </div>
        {dayEvents.length === 0 && (
          <p className="text-sm text-muted-foreground italic text-center mt-2">Nothing planned. Tap any hour to add something.</p>
        )}
      </div>
    </div>
  );
}

function DayEventBlock({ event, members, pxPerHour, navigate, onDrop }) {
  const s = new Date(event.start_at);
  const eend = event.end_at ? new Date(event.end_at) : new Date(s.getTime() + 60*60000);
  // Grid starts at 00:00, so top offset = wall-clock minutes since midnight.
  const startMin = Math.max(0, s.getHours() * 60 + s.getMinutes());
  const dur = Math.max(20, ((eend - s) / 60000));
  const top = (startMin / 60) * pxPerHour;
  const height = (dur / 60) * pxPerHour;
  const part = (event.participant_ids || []).map((id) => members.find((m) => m.id === id)?.color).filter(Boolean);
  const drag = useLongPressDrag({
    payload: { eventId: event.id },
    onDrop,
    label: event.title,
  });
  const onClick = () => { if (!drag.dragging) navigate(calendarItemRoute(event)); };
  return (
    <div
      data-testid={`day-event-${event.id}`}
      {...drag.bind}
      onClick={onClick}
      className="absolute left-2 right-2 rounded-lg p-2 text-left tap overflow-hidden text-white cursor-grab active:cursor-grabbing pointer-events-auto"
      style={{ top, height, minHeight: 28, ...(drag.bind.style || {}) }}
      title={event.title}
    >
      <span className="absolute inset-0 flex">
        {(part.length ? part : ["#888"]).map((c, i) => (
          <span key={i} className="flex-1" style={{ backgroundColor: c }} />
        ))}
      </span>
      <span className="relative font-medium text-[12px] truncate block" style={{ textShadow: "0 1px 1px rgba(0,0,0,.35)" }}>
        {event.title}
      </span>
      <span className="relative text-[10px] opacity-90 block">{fmtTime(event.start_at)}{event.location_name ? ` · ${event.location_name}` : ""}</span>
    </div>
  );
}

function MonthView({ events, members, onDayTap }) {
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [daySheet, setDaySheet] = useState(null);
  const year = cursor.getFullYear(); const month = cursor.getMonth();
  const firstDay = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const ymd = (d) => localYmd(d);

  const byDay = useMemo(() => {
    const m = new Map();
    for (const e of events) {
      const start = eventLocalYmd(e.start_at);
      const end = eventLocalYmd(e.end_at || e.start_at);
      if (!start) continue;
      const s = new Date(start + "T00:00:00");
      const ed = new Date((end || start) + "T00:00:00");
      const totalDays = Math.max(1, Math.round((ed - s) / 86400000) + 1);
      for (let i = 0; i < Math.min(totalDays, 14); i++) {
        const d = new Date(s); d.setDate(s.getDate() + i);
        const k = ymd(d);
        const arr = m.get(k) || [];
        arr.push({ ...e, _isMultiDay: totalDays > 1, _dayIndex: i, _totalDays: totalDays });
        m.set(k, arr);
      }
    }
    return m;
  }, [events]);

  const stripMonths = useMemo(() => Array.from({ length: 7 }, (_, i) => new Date(year, month - 2 + i, 1)), [year, month]);
  const isThisMonth = (d) => d.getMonth() === new Date().getMonth() && d.getFullYear() === new Date().getFullYear();
  const goMonth = (delta) => setCursor(new Date(year, month + delta, 1));
  const jumpToToday = () => { const d = new Date(); d.setDate(1); setCursor(d); };

  const daySheetEvents = daySheet
    ? (byDay.get(ymd(daySheet)) || []).slice().sort((a, b) => (a.start_at || "").localeCompare(b.start_at || ""))
    : [];

  return (
    <div data-testid="month-view">
      <div className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-2 scrollbar-hide" data-testid="month-strip">
        {stripMonths.map((d) => {
          const isCurrent = d.getMonth() === month && d.getFullYear() === year;
          return (
            <button
              key={d.toISOString()}
              data-testid={`strip-${d.getFullYear()}-${d.getMonth()+1}`}
              onClick={() => setCursor(d)}
              className={[
                "shrink-0 h-8 px-3.5 rounded-full tap text-[12px] font-medium border",
                isCurrent ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-foreground",
              ].join(" ")}
            >
              {d.toLocaleDateString(undefined, { month: "short" })}
              <span className={isCurrent ? "ml-1 opacity-80" : "ml-1 text-muted-foreground"}>
                {String(d.getFullYear()).slice(2)}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between mt-1 mb-2 px-1">
        <button data-testid="month-prev" onClick={() => goMonth(-1)} className="w-9 h-9 tap rounded-full inline-flex items-center justify-center text-muted-foreground hover:bg-secondary" aria-label="Previous month">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h3 className="font-display text-[17px] font-medium tracking-tight">
          {cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
        </h3>
        <div className="flex items-center gap-1">
          {!isThisMonth(cursor) && (
            <button data-testid="month-today" onClick={jumpToToday} className="h-8 px-3 rounded-full bg-secondary text-foreground text-[12px] font-medium tap">
              Today
            </button>
          )}
          <button data-testid="month-next" onClick={() => goMonth(1)} className="w-9 h-9 tap rounded-full inline-flex items-center justify-center text-muted-foreground hover:bg-secondary" aria-label="Next month">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-px text-[10px] text-muted-foreground uppercase tracking-wider mb-1">
        {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d, i) => (
          <div key={i} className="text-center pb-1">{d.charAt(0)}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-px bg-border/40 rounded-xl overflow-hidden border border-border/40">
        {cells.map((date, i) => {
          if (!date) return <div key={i} className="bg-card min-h-[78px]" />;
          const items = byDay.get(ymd(date)) || [];
          const isToday = date.toDateString() === new Date().toDateString();
          const isWeekend = date.getDay() === 0 || date.getDay() === 6;
          return (
            <button
              key={i}
              data-testid={`month-cell-${ymd(date)}`}
              onClick={() => onDayTap?.(ymd(date))}
              className={[
                "min-h-[78px] p-1 text-left tap flex flex-col gap-0.5",
                isWeekend ? "bg-secondary/40" : "bg-card",
              ].join(" ")}
            >
              <div className="flex items-center justify-start">
                <span
                  className={[
                    "text-[11px] tabular-nums w-5 h-5 inline-flex items-center justify-center rounded-full",
                    isToday ? "bg-primary text-primary-foreground font-semibold" : "text-foreground/80",
                  ].join(" ")}
                >
                  {date.getDate()}
                </span>
              </div>
              <div className="flex flex-col gap-[2px] mt-0.5 min-w-0">
                {items.slice(0, 3).map((e, idx) => {
                  const partColors = (e.participant_ids || [])
                    .map((id) => members.find((m) => m.id === id)?.color)
                    .filter(Boolean);
                  const fallback = "#888";
                  return (
                    <div
                      key={`${e.id}-${idx}`}
                      className="relative text-[9px] leading-[1.15] truncate rounded-[3px] overflow-hidden h-[14px]"
                      title={e.title}
                    >
                      <span className="absolute inset-0 flex">
                        {(partColors.length ? partColors : [fallback]).slice(0, 4).map((c, i) => (
                          <span key={i} className="flex-1" style={{ backgroundColor: c }} />
                        ))}
                      </span>
                      <span
                        className="relative px-1 py-[1px] block text-white font-medium truncate"
                        style={{ textShadow: "0 1px 1px rgba(0,0,0,.35)" }}
                      >
                        {e._isMultiDay && e._dayIndex > 0 ? "↳ " : ""}{e.title}
                      </span>
                    </div>
                  );
                })}
                {items.length > 3 && (
                  <span className="text-[9px] text-muted-foreground px-1">+{items.length - 3}</span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {daySheet && (
        <DayDetailSheet
          date={daySheet}
          events={daySheetEvents}
          members={members}
          onClose={() => setDaySheet(null)}
          onEvent={(e) => { setDaySheet(null); navigate(calendarItemRoute(e)); }}
        />
      )}
    </div>
  );
}

function DayDetailSheet({ date, events, members, onClose, onEvent }) {
  const title = date.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });
  return (
    <BottomSheet open={true} onClose={onClose} title={title}>
      <div data-testid="day-detail-sheet" className="space-y-3 pb-2">
        {events.length === 0 ? (
          <p className="text-sm text-muted-foreground italic text-center py-6">
            Nothing planned. A blank day is a gift.
          </p>
        ) : (
          <ul className="divide-y divide-border/60">
            {events.map((e) => (
              <li key={e.id}>
                <button
                  data-testid={`day-event-${e.id}`}
                  onClick={() => onEvent(e)}
                  className="w-full flex items-start gap-3 py-3 tap text-left"
                >
                  <span
                    className="w-1 self-stretch rounded-full shrink-0"
                    style={{ backgroundColor: memberColor(members, e.participant_ids?.[0]) }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-[15px] font-medium truncate">{e.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {e.all_day || e._isMultiDay ? "all-day" : fmtTime(e.start_at)}
                      {e.location_name ? ` · ${e.location_name}` : ""}
                    </p>
                    {(e.participant_ids || []).length > 0 && (
                      <div className="flex items-center gap-1 mt-1.5">
                        {(e.participant_ids || []).slice(0, 4).map((pid) => {
                          const m = members.find((x) => x.id === pid);
                          if (!m) return null;
                          return (
                            <span key={pid} className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                              <MemberAvatar member={m} size={16} ring={false} /> {m.display_name?.split(" ")[0]}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground mt-1" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </BottomSheet>
  );
}
